import { useState } from 'react';
import { AiDynamicSectionMediaPool } from './AiDynamicSectionMediaPool';
import { AiDynamicSectionRespondentData } from './AiDynamicSectionRespondentData';
import { AiDynamicSectionRubricChecklist } from './AiDynamicSectionRubricChecklist';
import { AiDynamicSectionRubricEditor } from './AiDynamicSectionRubricEditor';
import { AiDynamicSectionRubricStaleNotice } from './AiDynamicSectionRubricStaleNotice';
import {
  ALLOWED_EMIT_TYPES,
  DEFAULT_SECTION_LIMITS,
  effectiveRubricStatus,
  type AiDynamicSectionConfig,
  type AiDynamicSectionLimits,
} from './aiDynamicSection';

/** One earlier question offered as a context-binding candidate: enough to look it up by id and
 *  show the author what they're picking (defect 1 — binding must go by id, never by a typed
 *  question number, and the author must see the question's text to pick correctly). */
export interface AiDynamicSectionAvailableQuestion {
  id: string;
  text: string;
}

export interface AiDynamicSectionSettingsProps {
  config: AiDynamicSectionConfig;
  onChange: (config: AiDynamicSectionConfig) => void;
  /** Questions already authored earlier in the survey, offered as context-binding candidates.
   *  Always bound by id — there is no free-text fallback. Empty/omitted renders a plain
   *  "no earlier questions" message instead of an empty picker. */
  availableQuestions?: AiDynamicSectionAvailableQuestion[];
  /** Regenerates the rubric from the section's current goal/topics/instructions/linked questions.
   *  Omit in any context that cannot perform the call (e.g. a read-only preview) — the panel does
   *  no data fetching of its own, so with this absent the regenerate/keep actions do not render. */
  onRegenerateRubric?: () => Promise<void>;
  /** Dismisses the staleness notice, telling the caller the researcher chose to keep the rubric
   *  as it is. Omit alongside `onRegenerateRubric` in a context that cannot act on the choice. */
  onKeepRubric?: () => void;
  /** True once client-side polling for a `pending` rubric gave up waiting — displayed the same as
   *  a backend-reported `failed`. See `AiDynamicSectionRubricChecklist`. */
  rubricPollTimedOut?: boolean;
  /** Null until the survey is saved; respondent data can only be uploaded to a saved survey. */
  surveyId?: number | null;
}

const LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';
const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const EMIT_TYPE_LABELS: Record<(typeof ALLOWED_EMIT_TYPES)[number], string> = {
  open_text: 'Open text',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  rating_scale: 'Rating scale',
  matrix_rating: 'Matrix rating',
  percentage_allocation: 'Percentage allocation',
};

const NO_EARLIER_QUESTIONS_TEXT =
  'No earlier questions in this survey yet — add one before this section to link its context to it.';

/** Says plainly what happens instead of leaving a limit field blank and mysterious (product
 *  owner: these fields "allow them to be null if researcher doesnt want to specify"). */
const limitDefaultHelperText = (defaultValue: number): string =>
  `Optional — leave blank and the platform default (${defaultValue}) applies.`;

/** One row per limit field, in display order. Kept local to this file rather than shared with
 *  `AiDynamicSectionSummary` (which needs label/default but not id/min) — same pattern this file
 *  already uses for `EMIT_TYPE_LABELS`, duplicated rather than abstracted across the two files. */
interface LimitFieldSpec {
  field: keyof AiDynamicSectionLimits;
  id: string;
  label: string;
  min: number;
  defaultValue: number;
}

const LIMIT_FIELDS: LimitFieldSpec[] = [
  {
    field: 'min_questions',
    id: 'ads-min-q',
    label: 'Min questions',
    min: 1,
    defaultValue: DEFAULT_SECTION_LIMITS.min_questions,
  },
  {
    field: 'max_questions',
    id: 'ads-max-q',
    label: 'Max questions',
    min: 1,
    defaultValue: DEFAULT_SECTION_LIMITS.max_questions,
  },
  {
    field: 'advisory_seconds',
    id: 'ads-advisory-s',
    label: 'Advisory seconds',
    min: 0,
    defaultValue: DEFAULT_SECTION_LIMITS.advisory_seconds,
  },
  {
    field: 'hard_ceiling_seconds',
    id: 'ads-ceiling-s',
    label: 'Hard ceiling seconds',
    min: 0,
    defaultValue: DEFAULT_SECTION_LIMITS.hard_ceiling_seconds,
  },
];

const RUBRIC_REGENERATE_WORKING_TEXT = 'Regenerating…';
const RUBRIC_REGENERATE_ERROR_TEXT =
  "Couldn't regenerate the checklist. The one below is unchanged — try again, or keep it as is.";
/** The failed-rubric-derivation retry button (`rubric_status === 'failed'`, or a client-side poll
 *  that gave up on `pending`) is worded distinctly from the stale-notice one above: nothing was
 *  ever written here to "regenerate" — this is the first attempt succeeding. */
const RUBRIC_RETRY_BUTTON_TEXT = 'Try again';

/** Both actions are equally legitimate — regenerating loses nothing the author wrote (the rubric
 *  is AI-derived), and keeping is right when the underlying edit was cosmetic — so neither button
 *  gets the "dangerous" (red/destructive) treatment the rest of this file reserves for deletes. */
const RUBRIC_STALE_ACTION_BUTTON_CLASS =
  'px-3 py-1.5 bg-white border border-amber-300 rounded-md text-sm font-medium text-amber-900 ' +
  'hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed';

/**
 * Authoring editor for one `ai_dynamic_section` question's config (spec-01 "Authoring contract").
 *
 * No authored question text here by design (requirement 1: goal + topics + rubric only). The
 * rubric arrives pre-populated by `RubricDerivationService` at save time — it stays AI-generated,
 * never author-built from scratch (the author's own words were "it should be given by AI not the
 * author"), but once generated the author may correct a point's text or acceptance-bar guidance,
 * or add/remove a point, via `AiDynamicSectionRubricEditor` below. Tier stays read-only for now.
 * None of that touches staleness: the backend fingerprint that decides `rubric_stale` deliberately
 * excludes the rubric itself, so editing a point here never marks the section stale or triggers a
 * regeneration. `probing_instructions` stays the one field the author has always written directly:
 * it is HOW the section probes, not WHAT it covers.
 *
 * A controlled component: it owns no server state, just `config` in / `onChange` out, so it can
 * be dropped into the survey builder's question editor without this file knowing anything about
 * how or when a save happens.
 */
export function AiDynamicSectionSettings({
  config,
  onChange,
  availableQuestions,
  onRegenerateRubric,
  onKeepRubric,
  rubricPollTimedOut,
  surveyId = null,
}: AiDynamicSectionSettingsProps) {
  const [newTopic, setNewTopic] = useState('');
  const [isRegeneratingRubric, setIsRegeneratingRubric] = useState(false);
  const [regenerateRubricError, setRegenerateRubricError] = useState<string | null>(null);

  // Direct point editing only makes sense once a checklist actually exists (the author's own
  // words: "after it has been generated") — while pending/failed/empty, the checklist area still
  // shows its own explanatory state via AiDynamicSectionRubricChecklist, unchanged.
  const rubricStatus = rubricPollTimedOut ? 'failed' : effectiveRubricStatus(config.rubric_status);
  const rubricIsEditable = rubricStatus === 'ready' && config.rubric.length > 0;

  const update = (patch: Partial<AiDynamicSectionConfig>) => onChange({ ...config, ...patch });

  const regenerateRubric = async () => {
    if (!onRegenerateRubric || isRegeneratingRubric) return;
    setIsRegeneratingRubric(true);
    setRegenerateRubricError(null);
    try {
      await onRegenerateRubric();
    } catch {
      setRegenerateRubricError(RUBRIC_REGENERATE_ERROR_TEXT);
    } finally {
      setIsRegeneratingRubric(false);
    }
  };

  const addTopic = () => {
    const trimmed = newTopic.trim();
    if (!trimmed) return;
    update({ topics: [...config.topics, trimmed] });
    setNewTopic('');
  };

  const removeTopic = (index: number) =>
    update({ topics: config.topics.filter((_, i) => i !== index) });

  const toggleEmitType = (type: string) => {
    const next = config.allowed_emit_types.includes(type)
      ? config.allowed_emit_types.filter((t) => t !== type)
      : [...config.allowed_emit_types, type];
    update({ allowed_emit_types: next });
  };

  // Empty means "not specified" — null, never 0 (a max_questions of 0 would be a section that
  // asks nothing) and never the empty string. Invalid, non-numeric input is ignored rather than
  // silently zeroed or nulled, leaving whatever the field held before the keystroke.
  const setLimit = (field: keyof AiDynamicSectionLimits, raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      update({ limits: { ...config.limits, [field]: null } });
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return;
    update({ limits: { ...config.limits, [field]: parsed } });
  };

  const toggleBoundQuestionId = (id: string) => {
    const current = config.context_bindings.question_ids;
    const next = current.includes(id) ? current.filter((q) => q !== id) : [...current, id];
    update({ context_bindings: { ...config.context_bindings, question_ids: next } });
  };

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="ads-goal" className={LABEL_CLASS}>
          Goal
        </label>
        <textarea
          id="ads-goal"
          value={config.goal}
          onChange={(e) => update({ goal: e.target.value })}
          className={INPUT_CLASS}
          rows={2}
          placeholder="What should this section understand about the respondent?"
        />
      </div>

      <div>
        <label htmlFor="ads-probing-instructions" className={LABEL_CLASS}>
          Probing instructions (optional)
        </label>
        <p className="text-xs text-gray-500 mb-1">
          Guidance on HOW to probe — pacing, tone, when to push harder — not what to ask about
          (that's Goal and Topics, above).
        </p>
        <textarea
          id="ads-probing-instructions"
          value={config.probing_instructions}
          onChange={(e) => update({ probing_instructions: e.target.value })}
          className={INPUT_CLASS}
          rows={2}
          placeholder='e.g. "Cover purchase drivers. If they are vague about price specifically, push there before moving on."'
        />
      </div>

      <div>
        <span className={LABEL_CLASS}>Topics</span>
        <div className="flex flex-wrap gap-2 mb-2">
          {config.topics.map((topic, index) => (
            <span
              key={`${topic}-${index}`}
              className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 text-sm px-3 py-1 rounded-full"
            >
              {topic}
              <button
                type="button"
                onClick={() => removeTopic(index)}
                aria-label={`Remove topic ${topic}`}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTopic();
              }
            }}
            placeholder="Add a topic"
            className={INPUT_CLASS}
          />
          <button
            type="button"
            onClick={addTopic}
            className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Add
          </button>
        </div>
      </div>

      <div>
        <span className={LABEL_CLASS}>Rubric</span>
        {config.rubric_stale && (
          <AiDynamicSectionRubricStaleNotice
            onRegenerateRubric={onRegenerateRubric}
            onKeepRubric={onKeepRubric}
          />
        )}
        {rubricIsEditable ? (
          <AiDynamicSectionRubricEditor
            rubric={config.rubric}
            onChange={(rubric) => update({ rubric })}
          />
        ) : (
          <AiDynamicSectionRubricChecklist
            rubric={config.rubric}
            rubricStatus={config.rubric_status}
            pollTimedOut={rubricPollTimedOut}
            actions={
              onRegenerateRubric && (
                <button
                  type="button"
                  onClick={regenerateRubric}
                  disabled={isRegeneratingRubric}
                  className={RUBRIC_STALE_ACTION_BUTTON_CLASS}
                >
                  {isRegeneratingRubric ? RUBRIC_REGENERATE_WORKING_TEXT : RUBRIC_RETRY_BUTTON_TEXT}
                </button>
              )
            }
          />
        )}
        {regenerateRubricError && (config.rubric_status === 'failed' || rubricPollTimedOut) && (
          <p role="alert" className="mt-2 text-xs text-red-700">
            {regenerateRubricError}
          </p>
        )}
      </div>

      <div>
        <span className={LABEL_CLASS}>Context from earlier questions</span>
        {availableQuestions && availableQuestions.length > 0 ? (
          <div className="flex flex-col gap-2">
            {availableQuestions.map((q) => (
              <label key={q.id} className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                <input
                  type="checkbox"
                  className="shrink-0"
                  checked={config.context_bindings.question_ids.includes(q.id)}
                  onChange={() => toggleBoundQuestionId(q.id)}
                />
                <span className="truncate max-w-md" title={q.text}>
                  {q.text}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">{NO_EARLIER_QUESTIONS_TEXT}</p>
        )}
        {/* A binding to a question the respondent skipped or never reached contributes nothing
            at runtime (spec-01 edge case) — nothing to validate here, the prober falls back to
            goal + transcript alone. */}
      </div>

      <AiDynamicSectionMediaPool
        mediaPool={config.media_pool}
        onChange={(media_pool) => update({ media_pool })}
      />

      <AiDynamicSectionRespondentData
        surveyId={surveyId}
        binding={config.respondent_data ?? null}
        onChange={(respondent_data) => update({ respondent_data })}
      />

      <div className="grid grid-cols-2 gap-4">
        {LIMIT_FIELDS.map(({ field, id, label, min, defaultValue }) => (
          <div key={field}>
            <label htmlFor={id} className={LABEL_CLASS}>
              {label}
            </label>
            <input
              id={id}
              type="number"
              min={min}
              value={config.limits[field] ?? ''}
              onChange={(e) => setLimit(field, e.target.value)}
              className={INPUT_CLASS}
            />
            <p className="text-xs text-gray-500 mt-1">{limitDefaultHelperText(defaultValue)}</p>
          </div>
        ))}
      </div>

      <div>
        <span className={LABEL_CLASS}>Question types the prober may ask</span>
        {/* max_diff and conjoint_analysis are never offered here — spec-01 requirement 5 bans
            both as probe emissions (too long for one turn). */}
        <div className="flex flex-wrap gap-3">
          {ALLOWED_EMIT_TYPES.map((type) => (
            <label key={type} className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={config.allowed_emit_types.includes(type)}
                onChange={() => toggleEmitType(type)}
              />
              {EMIT_TYPE_LABELS[type]}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
